import {
  Col,
  Input,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import VuiPageTitle from "../../../components/page-title";
import { useTranslation } from "react-i18next";
import VuiButton from "../../../components/button";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table/interface";
import { ProjectDataList } from "./interface";
import VuiTableActionButton from "../../../components/table-action-button";
import { useGetProject } from "../hooks/use-get-project";
import VuiAccessible from "../../../components/accessible";
import { IPaginationParams } from "../../../utils/interfaces/pagination-params.interface";
import { overrideTableSortOrder, uiFilterData } from "../../../utils/helpers";
import VuiSelect from "../../../components/select";
import { ValueType } from "../../../components/select/interface";
import {
  DEFAULT_PAGE_SIZE,
  LOCALSTORAGE_FILTER_DATA_KEY,
} from "../../../constants";
import _ from "lodash";
import useDebounce from "../../../hooks/use-debounce";
import CustomerRepository from "../../../repositories/customer-repository";
import UserRepository from "../../../repositories/user-repository";
import ProjectTypeRepository from "../../../repositories/project-type-repository";
import { ProjectCategoryEnum } from "../../../models/project";

const { Text } = Typography;

const qs = require("qs-lite");

type ProjectTabType = "internal" | "google_workspace";

const ProjectListModule = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, loadData, isOnFetchingData, totalData } = useGetProject();
  const [hasInitialize, setHasInitialize] = useState<boolean>(false);

  const [selectedCustomer, setSelectedCustomer] = useState<
    ValueType | undefined
  >();

  const [selectedProjectManager, setSelectedProjectManager] = useState<
    ValueType | undefined
  >();

  const [selectedType, setSelectedType] = useState<ValueType | undefined>();

  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 250);

  const [searchParams, setSearchParams] = useSearchParams();
  const [tableQuery, setTableQuery] = useState<IPaginationParams>({
    page: Number(searchParams.get("page")) || 1,
    per_page: Number(searchParams.get("perPage")) || DEFAULT_PAGE_SIZE,
    sorted_by: searchParams.get("sortedBy"),
    order_by: searchParams.get("orderBy") as string,
    with: ["assignees.user", "gwProduct"],
  });

  const [selectedTab, setSelectedTab] = useState<ProjectTabType>(
    (searchParams.get("tab") as ProjectTabType) || "internal"
  );

  const handleChangeSearchParams = useCallback(async () => {
    const params = {} as IPaginationParams;

    Object.assign(params, {
      page: tableQuery.page,
    });

    Object.assign(params, {
      perPage: tableQuery.per_page,
    });

    Object.assign(params, {
      tab: selectedTab,
    });

    if (debouncedSearch) {
      Object.assign(params, {
        search: debouncedSearch,
      });
    }

    if (tableQuery.order_by) {
      Object.assign(params, {
        orderBy: tableQuery.order_by,
      });
    }

    if (tableQuery.sorted_by) {
      Object.assign(params, {
        sortedBy: tableQuery.sorted_by,
      });
    }

    if (selectedCustomer) {
      Object.assign(params, {
        customer: selectedCustomer.value,
      });
    } else {
      const recentCustomer = uiFilterData.get(
        LOCALSTORAGE_FILTER_DATA_KEY.customer
      );
      if (!recentCustomer) {
        searchParams.delete("customer");
      } else {
        const paramsCustomerIds = _.map(
          searchParams.getAll("customer"),
          (item) => parseInt(item)
        );

        if (paramsCustomerIds.length) {
          const customerFromStorage = uiFilterData.getSelectedFilter(
            recentCustomer,
            paramsCustomerIds
          );

          setSelectedCustomer(customerFromStorage[0]);

          Object.assign(params, {
            customer: customerFromStorage[0]?.value,
          });
        } else {
          uiFilterData.remove(LOCALSTORAGE_FILTER_DATA_KEY.customer);
        }
      }
    }

    if (selectedType) {
      Object.assign(params, {
        type: selectedType.value,
      });
    } else {
      const recentType = uiFilterData.get(LOCALSTORAGE_FILTER_DATA_KEY.type);
      if (!recentType) {
        searchParams.delete("type");
      } else {
        const paramsTypeIds = _.map(searchParams.getAll("type"), (item) =>
          parseInt(item)
        );

        if (paramsTypeIds.length) {
          const typeFromStorage = uiFilterData.getSelectedFilter(
            recentType,
            paramsTypeIds
          );

          setSelectedType(typeFromStorage[0]);

          Object.assign(params, {
            type: typeFromStorage[0]?.value,
          });
        } else {
          uiFilterData.remove(LOCALSTORAGE_FILTER_DATA_KEY.type);
        }
      }
    }

    if (selectedProjectManager) {
      Object.assign(params, {
        project_manager: selectedProjectManager.value,
      });
    } else {
      const recentProjectManager = uiFilterData.get(
        LOCALSTORAGE_FILTER_DATA_KEY.projectManager
      );
      if (!recentProjectManager) {
        searchParams.delete("project_manager");
      } else {
        const paramsProjectManagerIds = _.map(
          searchParams.getAll("project_manager"),
          (item) => parseInt(item)
        );

        if (paramsProjectManagerIds.length) {
          const projectManagerFromStorage = uiFilterData.getSelectedFilter(
            recentProjectManager,
            paramsProjectManagerIds
          );

          setSelectedProjectManager(projectManagerFromStorage[0]);

          Object.assign(params, {
            project_manager: projectManagerFromStorage[0]?.value,
          });
        } else {
          uiFilterData.remove(LOCALSTORAGE_FILTER_DATA_KEY.projectManager);
        }
      }
    }

    const queryParams = qs.stringify(params, { indices: false });

    if (queryParams) {
      setSearchParams(`?${queryParams}`);
    } else {
      navigate("/project");
    }

    const tableParams: IPaginationParams = {
      page: params.page,
      per_page: tableQuery.per_page,
      tab: selectedTab,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(tableQuery.order_by ? { order_by: tableQuery.order_by } : {}),
      ...(tableQuery.sorted_by
        ? { sorted_by: overrideTableSortOrder(tableQuery.sorted_by) }
        : {}),
      ...(params.customer ? { customer: params.customer } : {}),
      ...(params.project_manager
        ? { project_manager: params.project_manager }
        : {}),
      ...(params.type ? { type: params.type } : {}),
      for_internal: selectedTab === "internal",
      for_google_workspace: selectedTab === "google_workspace",
      with: tableQuery.with,
    };

    await loadData(tableParams);

    setHasInitialize(true);
  }, [
    tableQuery,
    debouncedSearch,
    selectedCustomer,
    selectedProjectManager,
    loadData,
    navigate,
    selectedType,
    searchParams,
    setSearchParams,
  ]);

  const columnsProject: ColumnsType<ProjectDataList> = [
    {
      title: "ID",
      dataIndex: "code",
      key: "code",
    },
    {
      title: t("common.text.type"),
      dataIndex: "type_name",
      key: "type_name",
      sorter: true,
      defaultSortOrder:
        tableQuery.order_by === "type_name" && tableQuery.sorted_by,
      render: (text, record) => (text ? text : "Google Workspace"),
    },
    {
      title: t("common.text.client"),
      dataIndex: "customer_name",
      key: "customer_name",
      sorter: true,
      defaultSortOrder:
        tableQuery.order_by === "customer_name" && tableQuery.sorted_by,
      render: (text, record) => {
        let name = "";
        let tooltip = null;
        if (record.assignees.length > 1) {
          name = `${record.assignees[0].user?.initial} +${record.assignees.length - 1
            }`;
          tooltip = record.assignees.map((a: any) => a.user?.name).join(", ");
        } else if (record.assignees.length === 1) {
          name = record.assignees[0].user?.initial;
          tooltip = record.assignees[0].user?.name;
        }

        return (
          <Space style={{ whiteSpace: "nowrap" }}>
            <Text>{text}</Text>
            {name ? (
              tooltip ? (
                <Tooltip title={tooltip}>
                  <Tag style={{ color: "#757575" }}>{name}</Tag>
                </Tooltip>
              ) : (
                <Tag style={{ color: "#757575" }}>{name}</Tag>
              )
            ) : null}
          </Space>
        );
      },
    },
    {
      title: t("common.text.description"),
      dataIndex: "description",
      key: "description",
      sorter: true,
      defaultSortOrder:
        tableQuery.order_by === "description" && tableQuery.sorted_by,
      render: (text) => (text ? text : "-"),
    },
    {
      title: t("common.text.action"),
      key: "id",
      className: "table-action",
      fixed: "right",
      width: 110,
      render: (text, record) => {
        return (
          <VuiTableActionButton
            detailLink={`/project/${record.id}`}
            detailPermissionName="project.show"
            deletePermissionName="project.destroy"
            showDetail={record.can_show}
            showDelete={record.can_delete}
          />
        );
      },
    },
  ];

  const columnsGoogleWorkspace: ColumnsType<ProjectDataList> = [
    {
      title: "ID",
      dataIndex: "code",
      key: "code",
    },
    {
      title: t("common.text.client"),
      dataIndex: "customer_name",
      key: "customer_name",
      sorter: true,
      defaultSortOrder:
        tableQuery.order_by === "customer_name" && tableQuery.sorted_by,
      render: (text, record) => {
        let name = "";
        let tooltip = null;
        if (record.assignees.length > 1) {
          name = `${record.assignees[0].user.initial} +${record.assignees.length - 1
            }`;
          tooltip = record.assignees.map((a) => a.user.name).join(", ");
        } else if (record.assignees.length === 1) {
          name = record.assignees[0].user.initial;
          tooltip = record.assignees[0].user.name;
        }

        return (
          <Space style={{ whiteSpace: "nowrap" }}>
            <Text>{text}</Text>
            {name ? (
              tooltip ? (
                <Tooltip title={tooltip}>
                  <Tag style={{ color: "#757575" }}>{name}</Tag>
                </Tooltip>
              ) : (
                <Tag style={{ color: "#757575" }}>{name}</Tag>
              )
            ) : null}
          </Space>
        );
      },
    },
    {
      title: t("common.text.product"),
      dataIndex: "product_name",
      key: "product_name",
      render: (text, record) => record.gw_product?.sku_name,
    },
    {
      title: t("common.text.action"),
      key: "id",
      className: "table-action",
      fixed: "right",
      width: 110,
      render: (text, record) => {
        return (
          <VuiTableActionButton
            detailLink={`/project/${record.id}`}
            detailPermissionName="project.show"
            deletePermissionName="project.destroy"
            showDetail={record.can_show}
            showDelete={record.can_delete}
          />
        );
      },
    },
  ];

  const handleTabChange = (key: string) => {
    setSelectedTab(key as ProjectTabType);
    setSearch("");
    handleOnChangeCustomer(undefined as unknown as ValueType);
    handleOnChangeProjectManager(undefined as unknown as ValueType);
    handleOnChangeType(undefined as unknown as ValueType);

    setTableQuery((prevState) => ({
      ...prevState,
      page: 1,
      tab: key,
    }));
  };

  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      const params: IPaginationParams = {
        page: pagination.current,
        per_page: pagination.pageSize,
        with: tableQuery.with,
      };

      if (sorter.order) {
        Object.assign(params, {
          sorted_by: sorter.order,
          order_by: sorter.field,
        });
      } else {
        Object.assign(params, {
          sorted_by: null,
          order_by: null,
        });
      }

      setTableQuery(params);
    },
    []
  );

  const handleOnChangeType = useCallback((value: ValueType) => {
    uiFilterData.save(LOCALSTORAGE_FILTER_DATA_KEY.type, [value]);
    setSelectedType(value);
    setTableQuery((prevState) => ({
      ...prevState,
      page: 1,
      type: value,
    }));
  }, []);

  const handleOnChangeCustomer = useCallback((value: ValueType) => {
    uiFilterData.save(LOCALSTORAGE_FILTER_DATA_KEY.customer, [value]);
    setSelectedCustomer(value);
    setTableQuery((prevState) => ({
      ...prevState,
      page: 1,
      customer: value,
    }));
  }, []);

  const handleOnChangeProjectManager = useCallback((value: ValueType) => {
    uiFilterData.save(LOCALSTORAGE_FILTER_DATA_KEY.projectManager, [value]);
    setSelectedProjectManager(value);
    setTableQuery((prevState) => ({
      ...prevState,
      page: 1,
      project_manager: value,
    }));
  }, []);

  useEffect(() => {
    (async () => {
      await handleChangeSearchParams();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableQuery]);

  useEffect(() => {
    if (hasInitialize) {
      setTableQuery((prevState) => ({
        ...prevState,
        search: debouncedSearch,
        page: 1,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return (
    <>
      <VuiPageTitle title={t("pages.project.list.title")}>
        <VuiAccessible access="project.store">
          <Link to={`/project/add/${selectedTab}`}>
            <VuiButton
              title={t("common.button.addItem", {
                item: t("common.text.project"),
              })}
              buttonProps={{
                icon: <PlusOutlined />,
                size: "middle",
              }}
            />
          </Link>
        </VuiAccessible>
      </VuiPageTitle>

      <Row gutter={[16, 16]} className="mb16">
        <Col className="gutter-row" xs={24} md={8}>
          <Input
            allowClear
            placeholder={t("common.form.search.placeholder")}
            prefix={<SearchOutlined />}
            value={search}
            size="middle"
            onChange={(value) => {
              setSearch(value.target.value);
            }}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mb16">
        {selectedTab === "internal" ? (
          <Col className="gutter-row" xs={24} md={4}>
            <VuiSelect
              repository={ProjectTypeRepository}
              value={selectedType}
              placeholder={t("common.text.selectType")}
              onChange={handleOnChangeType}
              className="select-filter"
              size="middle"
            />
          </Col>
        ) : null}
        <Col className="gutter-row" xs={24} md={6}>
          <VuiSelect
            repository={CustomerRepository}
            value={selectedCustomer}
            placeholder={t("common.text.selectClient")}
            onChange={handleOnChangeCustomer}
            className="select-filter"
            size="middle"
          />
        </Col>
        <Col className="gutter-row" xs={24} md={6}>
          <VuiSelect
            repository={UserRepository}
            value={selectedProjectManager}
            placeholder={t("common.text.selectProjectManager")}
            onChange={handleOnChangeProjectManager}
            className="select-filter"
            size="middle"
          />
        </Col>
      </Row>

      <Tabs defaultActiveKey={selectedTab} onChange={handleTabChange}>
        <Tabs.TabPane tab={t("common.text.project")} key="internal">
          <Table
            rowKey={"id"}
            columns={columnsProject}
            dataSource={data}
            loading={isOnFetchingData}
            onChange={handleTableChange}
            pagination={{
              current: tableQuery.page,
              showSizeChanger: true,
              pageSize: tableQuery.per_page,
              total: totalData,
            }}
            size="small"
            scroll={{ x: "max-content" }}
          />
        </Tabs.TabPane>
      </Tabs>
    </>
  );
};

export default ProjectListModule;
